/**
 * zeluslugi.ru
 * @author      delphinpro <delphinpro@gmail.com>
 * @copyright   copyright © 2018 delphinpro
 * @license     licensed under the MIT license
 *
 * @external $
 */

import VueSlider from 'vue-slider-component';
import IconPlus from './IconPlus';
import CalcSliderControl from './CalcSliderControl';
import CalcCheckboxControl from './CalcCheckboxControl';
import CalcRadioControl from './CalcRadioControl';
import CalcStaticInfo from './CalcStaticInfo';
import CalcTooltip from './CalcTooltip';
import { calcRound } from '../library/calcRound';
import { triplets } from '../library/triplets';
import {
    CALC_DISPLAY_AS_FULL,
    CALC_DISPLAY_AS_MINI,
    CALC_DISPLAY_MODE_COMPACT,
    CALC_DISPLAY_MODE_DETAILS,
    EMERGENCY_TEXT_0,
    EMERGENCY_TEXT_1,
    EMERGENCY_TEXT_2,
} from './calc-constants';
import { pluralize } from '../library/pluralize';

const defaultSliderOptions = {
    height        : 10,
    dotSize       : 28,
    speed         : 0.1,
    piecewiseLabel: true,
};

// noinspection JSUnusedGlobalSymbols
export default {
    name: 'calculator',

    components: {
        VueSlider,
        IconPlus,
        CalcSliderControl,
        CalcCheckboxControl,
        CalcRadioControl,
        CalcStaticInfo,
        CalcTooltip,
    },

    props: {
        displayAs : {
            type   : String,
            default: CALC_DISPLAY_AS_MINI,
        },
        yandexGoal: {
            type   : String,
            default: '',
        },
    },

    data: () => ({
        options    : defaultSliderOptions,
        init       : 0,
        displayMode: CALC_DISPLAY_MODE_COMPACT,

        displayMore1: false,
        displayMore2: false,

        stations         : 1,
        stationsHasAdmin : 0,
        support1C        : 0,
        additionalBases1C: 0,
        remoteAccess     : 0,
        speed            : 0,
        emergency        : 0,

        servers         : 0,
        virtual         : 0,
        domainController: false,
        serverSQL       : 0,
        server1C        : 0,
        localMailServer : 0,
        serviceWSUS     : false,
        antiSpam        : false,

        offices: 1,

        dvr             : 0,
        ipCamera        : 0,
        scheduledService: 0,

        p              : {
            minimum          : 6200,
            stations         : {
                title    : '',
                min      : 1,
                max      : 100,
                initValue: 0,
                sla      : [
                    {
                        k           : 740,
                        multiplier  : 2,
                        minimum     : 580,
                        minimumTotal: 0,
                    },
                ],
                minimum  : 580,
                tooltip  : false,
            },
            stationsHasAdmin : { title: '', initValue: 0, everyone: 0, tooltip: false },
            support1C        : { title: '', tooltip: false, options: [] },
            additionalBases1C: {
                title    : '',
                min      : 0,
                max      : 0,
                initValue: 0,
                everyone : 0,
                tooltip  : false,
            },
            remoteAccess     : {
                title    : '',
                min      : 0,
                max      : 100,
                initValue: 0,
                everyone : 80,
                tooltip  : false,
            },
            speed            : { title: '', tooltip: false, options: [] },
            emergency        : { title: '', tooltip: false, options: [] },
            servers          : {
                title    : '',
                min      : 0,
                max      : 100,
                initValue: 0,
                everyone : 0,
                one      : 0,
                tooltip  : false,
            },
            virtual          : { title: '', everyone: 0, tooltip: false },
            domainController : { title: '', everyoneDiscount: 0, tooltip: false },
            serverSQL        : { title: '', everyone: 0, tooltip: false },
            server1C         : { title: '', everyone: 0, tooltip: false },
            localMailServer  : { title: '', everyone: 0, tooltip: false },
            serviceWSUS      : { title: '', cost: 0, tooltip: false },
            antiSpam         : { title: '', cost: 0, tooltip: false },
            offices          : {
                title    : '',
                min      : 1,
                max      : 100,
                initValue: 0,
                everyone : 0,
                tooltip  : false,
            },
            dvr              : {
                title  : '',
                min    : 0,
                max    : 500,
                tooltip: false,
            },
            ipCamera         : {
                title  : '',
                min    : 0,
                max    : 500,
                tooltip: false,
            },
            scheduledService : {
                title  : '',
                min    : 0,
                max    : 23,
                tooltip: false,
            },
        },
        sections       : {
            supportStations: { title: '' },
            supportServers : { title: '' },
        },
        staticInfo     : [],
        includeServices: [],

        config: {},
    }),

    computed: {
        emergencyText() {
            switch (this.emergency) {
                case 0:
                    return EMERGENCY_TEXT_0;
                case 1:
                    return EMERGENCY_TEXT_1;
                case 2:
                    return EMERGENCY_TEXT_2;
            }
        },
        calcDisabled() {
            return this.init !== true;
        },
        computedCost() {
            if (!this.init) return this.p.minimum;

            let cost = this.costStations
                + this.costSupport1C
                + this.costAdditionalBases1C
                + this.costRemoteAccess
                + this.costEmergency

                + this.costServers
                + this.costVirtual
                + this.costDomainController
                + this.costServerSQL
                + this.costServer1C
                + this.costLocalMailServer
                + this.costServiceWSUS
                + this.costAntiSpam

                + this.costOffices

                + this.costDvr
                + this.costIpCamera
                + this.costScheduledService
            ;

            return Math.max(cost, this.p.minimum);
        },
        displayCost() {
            if (!this.computedCost) return 0;
            return triplets(Math.max(this.computedCost, this.p.minimum));
        },
        displayMinimum() {
            return triplets(this.p.minimum);
        },

        displayAdditionalHours() {
            return triplets(this.additionalHours) + ' ' +
                pluralize(this.additionalHours, ['час', 'часа', 'часов']);
        },

        additionalHours() {
            return Math.ceil((this.computedCost) / 10000) + 1;
        },

        totalMachines() {
            return this.stations + this.servers + this.virtual;
        },

        slaSpeed() {
            return this.p.stations.sla[this.speed];
        },
        slaEmergency() {
            return this.p.emergency.options[this.emergency];
        },

        costStations() {
            // n * ( k - multiplier * n )
            // n – количество станций (рабочих мест)
            // "k" и "multiplier" зависят от уровня SLA (скорости реакции), см ниже

            let n = this.stations;
            let k = this.slaSpeed.k;
            let multiplier = this.slaSpeed.multiplier;

            let total = n * (k - multiplier * n);
            if (total / n < this.slaSpeed.minimum) {
                total = this.slaSpeed.minimum * n;
            }
            if (total < this.slaSpeed.minimumTotal) {
                total = this.slaSpeed.minimumTotal;
            }

            if (this.stationsHasAdmin) {
                total += this.stationsHasAdmin * this.p.stationsHasAdmin.everyone;
            }

            return total;
        },
        costSupport1C() {
            if (0 <= this.support1C && this.support1C <= 2
                && this.support1C <= (this.p.support1C.options.length - 1)
                && this.totalMachines > 0
            ) {
                return this.p.support1C.options[this.support1C].cost;
            }

            return 0;
        },
        costAdditionalBases1C() {
            return (
                this.additionalBases1C
                && this.p.additionalBases1C.everyone
                && this.totalMachines > 0
                && !!this.support1C
            )
                ? this.additionalBases1C * this.p.additionalBases1C.everyone
                : 0;
        },
        costRemoteAccess() {
            return this.stations && this.remoteAccess && this.p.remoteAccess
                ? this.remoteAccess * this.p.remoteAccess.everyone
                : 0;
        },
        costEmergency() {
            if (!this.init) return 0;

            // n * (k – (multiplier * n))
            // n - количество станций
            // "k" и "multiplier" зависят от уровня SLA (скорости реакции), см ниже

            let n = this.stations;
            let k = this.slaEmergency.k;
            let multiplier = this.slaEmergency.multiplier;

            let total = n * (k - multiplier * n);
            if (total / n < this.slaEmergency.minimum) {
                total = this.slaEmergency.minimum * n;
            }
            if (total < this.slaEmergency.minimumTotal) {
                total = this.slaEmergency.minimumTotal;
            }

            return total;
        },

        costServers() {
            if (this.servers <= 0) return 0;

            return this.p.servers.one && this.servers === 1
                ? this.p.servers.one
                : this.servers * this.p.servers.everyone;
        },
        costVirtual() {
            if (this.servers && this.virtual && this.p.virtual.everyone) {
                return this.virtual * this.p.virtual.everyone;
            }

            return 0;
        },
        costDomainController() {
            return this.virtual && this.domainController && this.p.domainController
                ? this.virtual * this.p.domainController.everyoneDiscount
                : 0;
        },
        costServerSQL() {
            if (this.servers && this.serverSQL && this.p.serverSQL) {
                return this.serverSQL * this.p.serverSQL.everyone;
            }
            return 0;
        },
        costServer1C() {
            if (this.servers && this.server1C && this.p.server1C) {
                return this.server1C * this.p.server1C.everyone;
            }
            return 0;
        },
        costLocalMailServer() {
            if (this.servers && this.localMailServer && this.p.localMailServer) {
                return this.localMailServer * this.p.localMailServer.everyone;
            }
            return 0;
        },
        costServiceWSUS() {
            if (this.serviceWSUS && this.p.serviceWSUS.cost) {
                return this.p.serviceWSUS.cost;
            }
            return 0;
        },
        costAntiSpam() {
            if (this.localMailServer
                && this.antiSpam
                && this.p.antiSpam
                && this.p.antiSpam.cost
            ) {
                return this.p.antiSpam.cost;
            }
            return 0;
        },

        costOffices() {
            return this.offices > 0 ? this.offices * this.p.offices.everyone : 0;
        },

        costDvr() {
            return this.dvr > 0 ? this.dvr * this.p.dvr.everyone : 0;
        },
        costIpCamera() {
            return this.ipCamera > 0 ? this.ipCamera * this.p.ipCamera.everyone : 0;
        },
        costScheduledService() {
            return this.scheduledService > 0
                ? this.scheduledService * this.p.scheduledService.everyone : 0;
        },

        displayAsMini() {
            return this.displayAs === CALC_DISPLAY_AS_MINI;
        },
        displayAsFull() {
            return this.displayAs === CALC_DISPLAY_AS_FULL;
        },
        /**
         * @deprecated
         * @returns {boolean}
         */
        displayModeCompact() {
            // Компактный режим только в полном калькуляторе
            // noinspection JSUnresolvedVariable
            return this.displayMode === CALC_DISPLAY_MODE_COMPACT && this.displayAsFull;
        },
        /**
         * @deprecated
         * @returns {boolean}
         */
        displayModeDetails() {
            // Детальный режим только в полном калькуляторе
            // noinspection JSUnresolvedVariable
            return this.displayMode === CALC_DISPLAY_MODE_DETAILS && this.displayAsFull;
        },
        displayMoreStations() {
            // Детальный режим рабочих станций только в полном калькуляторе
            return this.displayMore1 && this.displayAsFull;
        },
        displayMoreServers() {
            // Детальный режим серверов только в полном калькуляторе
            return this.displayMore2 && this.displayAsFull;
        },

        layout() {
            return {
                showRight     : this.displayAsFull,
                leftClass     : {
                    'col-lg-7' : this.displayAsFull,
                    'col-xs-12': this.displayAsMini,
                },
                leftPanelClass: {
                    'calc__panel_color_blue': true, //this.displayAsMini || this.displayModeCompact,
                },
            };
        },

        isDev() {
            return process.env.NODE_ENV !== 'production';
        },
    },

    created() {
        $.ajax({
            url     : '/template/site/default/js/calc-data.php?t=' + (new Date()).getTime(),
            dataType: 'json',
        }).then(r => {

            // console.log('Calc\'s data loaded: ', r);

            this.stations = r.price.stations.initValue || 0;
            this.servers = r.price.servers.initValue || 0;
            this.offices = r.price.offices.initValue || 0;
            this.dvr = r.price.dvr.initValue || 0;
            this.ipCamera = r.price.ipCamera.initValue || 0;
            this.scheduledService = r.price.scheduledService.initValue || 0;
            this.domainController = r.price.domainController.initValue || false;

            this.p = r.price;
            this.sections = r.sections;
            this.staticInfo = r.staticInfo || [];
            this.includeServices = r.includeServices || [];
            this.init = true;

        }).catch(err => {

            console.error(err.status, err.statusText);

        });
    },

    methods: {
        emergencyDiscount(everyoneDiscount, maxDiscount) {
            let discount = calcRound(this.stations * everyoneDiscount);
            return discount <= maxDiscount ? maxDiscount : discount;
        },
        getData() {
            let data = {};

            if (this.stations > 0) {
                data.stations = {
                    price   : (this.costStations / this.stations).toFixed(2),
                    count   : this.stations,
                    discount: null,
                    sum     : this.costStations,
                };
            }

            if (this.stationsHasAdmin > 0) {
                data.localAdmin = {
                    price: this.p.stationsHasAdmin.everyone,
                    count: this.stationsHasAdmin,
                    sum  : this.stationsHasAdmin * this.p.stationsHasAdmin.everyone,
                };
            }

            if (this.costEmergency) {
                // let discount = this.emergencyDiscount(
                //     this.p.emergency.options[this.emergency].everyoneDiscount || 0,
                //     this.p.emergency.options[this.emergency].maxDiscount || 0,
                // );

                data.emergency = {
                    emergency: this.emergency,
                    price    : this.p.emergency.options[this.emergency].everyone,
                    count    : this.stations,
                    // discount : discount,
                    sum: this.costEmergency,
                };
            }

            if (this.costSupport1C) {
                data.support1c2 = {
                    sum: this.costSupport1C,
                };
            }

            if (this.costAdditionalBases1C) {
                data.support1cAdditional = {
                    price: this.p.additionalBases1C.everyone,
                    count: this.additionalBases1C,//this.totalMachines,//***
                    sum  : this.costAdditionalBases1C,
                };
            }

            if (this.costRemoteAccess) {
                data.remoteAccess = {
                    price: this.p.remoteAccess.everyone,
                    count: this.remoteAccess,
                    sum  : this.costRemoteAccess,
                };
            }

            data.speed = this.speed;

            if (this.costServers) {
                data.servers = {
                    price: this.p.servers.everyone,
                    count: this.servers,
                    sum  : this.costServers,
                };
                if (this.servers === 1 && this.p.servers.one) {
                    data.servers.price = this.p.servers.one;
                    data.servers.sum = this.p.servers.one;
                }
            }

            if (this.costVirtual) {
                data.virtual = {
                    price: this.p.virtual.everyone,
                    count: this.virtual,
                    sum  : this.costVirtual,
                };
            }

            if (this.costDomainController) {
                data.domain = {
                    price: this.p.domainController.everyoneDiscount,
                    count: this.virtual,
                    sum  : this.costDomainController,
                };
            }

            if (this.costServerSQL) {
                data.serverSQL = {
                    price: this.p.serverSQL.everyone,
                    count: this.serverSQL,
                    sum  : this.costServerSQL,
                };
            }

            if (this.costServer1C) {
                data.server1C = {
                    price: this.p.server1C.everyone,
                    count: this.server1C,
                    sum  : this.costServer1C,
                };
            }

            if (this.costLocalMailServer) {
                data.localMailServer = {
                    price: this.p.localMailServer.everyone,
                    count: this.localMailServer,
                    sum  : this.costLocalMailServer,
                };
            }

            if (this.costServiceWSUS) {
                data.serverWSUS = { sum: this.costServiceWSUS };
            }

            if (this.costAntiSpam) {
                data.antiSpam = { sum: this.costAntiSpam };
            }

            if (this.costOffices) {
                data.offices = {
                    price: this.p.offices.everyone,
                    count: this.offices,
                    sum  : this.costOffices,
                };
            }

            if (this.costDvr) {
                data.dvr = {
                    price: this.p.dvr.everyone,
                    count: this.dvr,
                    sum  : this.costDvr,
                };
            }

            if (this.costIpCamera) {
                data.ipCamera = {
                    price: this.p.ipCamera.everyone,
                    count: this.ipCamera,
                    sum  : this.costIpCamera,
                };
            }

            if (this.costScheduledService) {
                data.scheduledService = {
                    price: this.p.scheduledService.everyone,
                    count: this.scheduledService,
                    sum  : this.costScheduledService,
                };
            }

            return data;
        },

        onComplete() {

            let calcData = this.getData();
            // console.log('DATA', calcData);

            if (this.computedCost <= 0) {
                console.warn('Nothing selected');
                alert('Ничего не выбрано!');
                return;
            }

            let yandexGoal = this.yandexGoal;
            let calc = {
                stations: this.stations,
                servers : this.servers,
                offices : this.offices,
                sum     : this.displayCost,
            };

            let $Offer = $('#Offer');

            $Offer.find('form').data('goal', yandexGoal).attr('data-goal', yandexGoal);
            $Offer.find('.form-control').val('');
            $Offer.find('.Form').removeClass('FormComplete').removeClass('FormError');
            $Offer.find('.error').removeClass('error');
            $Offer.find('.has-danger').removeClass('has-danger');
            $Offer.find('.form-error').remove();

            $Offer.find('input[name="calcParams"]')
                .val(encodeURIComponent(JSON.stringify(calcData)));
            $Offer.find('input[name="stations"]').val(calc.stations);
            $Offer.find('input[name="servers"]').val(calc.servers);
            $Offer.find('input[name="offices"]').val(calc.offices);
            $Offer.find('input[name="sum"]').val(calc.sum + ' руб./мес');

            // if ($(ev.target).closest('.modal').length) {
            //     $(ev.target).closest('.modal').modal('hide');
            // }

            $Offer.modal('show');

            $Offer.one('hidden.bs.modal', () => {
                this.resetData({ target: { checked: true } });
            });
        },

        resetData(event) {
            if (event.target.checked) {
                this.displayMore1 = false;
                this.displayMore2 = false;

                this.stations = this.p.stations.min;
                this.stationsHasAdmin = 0;
                this.support1C = 0;
                this.additionalBases1C = 0;
                this.remoteAccess = 0;
                this.speed = 0;
                this.emergency = 0;

                this.servers = this.p.servers.min;
                this.virtual = 0;
                this.domainController = true;
                this.serverSQL = 0;
                this.server1C = 0;
                this.localMailServer = 0;
                this.serviceWSUS = false;
                this.antiSpam = false;

                this.offices = this.p.offices.min;
                this.dvr = this.p.dvr.min;
                this.ipCamera = this.p.ipCamera.min;
                this.scheduledService = this.p.scheduledService.min;
            }
        },
    },
};
